import { useRef } from "react";
import { Separator } from "react-resizable-panels";

const baseHandleStyle = {
  backgroundColor: "transparent",
  border: 0,
  boxShadow: "none",
  boxSizing: "border-box",
  position: "relative",
  zIndex: 5,
};

const baseGripStyle = {
  backgroundColor: "rgba(255, 255, 255, 0.28)",
  borderRadius: 999,
  boxShadow:
    "0 0 0 1px rgba(255, 255, 255, 0.18), 0 2px 6px rgba(0, 0, 0, 0.45)",
};

/**
 * A drag handle for a panel that is NOT inside a react-resizable-panels Group —
 * a content-sized modal, say, where percentage panels have nothing to be a
 * percentage of. Same grip as ResizablePanelHandle; the caller owns the height.
 *
 *   <DragResizeHandle value={height} onChange={(px) => setHeight(px)} onReset={...} />
 *
 * Pointer drag (mouse, touch, pen — `touchAction: none` keeps the page still),
 * arrow keys for the keyboard, double-click to reset. `onChange` receives the
 * proposed height in px; clamp it there.
 */
export const DragResizeHandle = ({
  value,
  onChange,
  onReset,
  ariaLabel = "Resize panel",
  step = 24,
  className = "",
  style,
}) => {
  const drag = useRef(null);

  const onPointerDown = (event) => {
    event.preventDefault();
    drag.current = { startY: event.clientY, startValue: value };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event) => {
    if (!drag.current) return;
    onChange(drag.current.startValue + (event.clientY - drag.current.startY));
  };
  const endDrag = (event) => {
    if (!drag.current) return;
    drag.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };
  const onKeyDown = (event) => {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      onChange(value - step);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      onChange(value + step);
    }
  };

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(value)}
      tabIndex={0}
      title="Drag to resize. Double-click to reset."
      className={`panel-resize-handle d-flex align-items-center justify-content-center ${className}`}
      style={{
        ...baseHandleStyle,
        height: "12px",
        minHeight: "12px",
        cursor: "row-resize",
        touchAction: "none",
        margin: "4px 0",
        ...style,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
    >
      <div className="panel-resize-handle__grip" style={{ ...baseGripStyle, width: "40px", height: "3px" }} />
    </div>
  );
};

/** Keep a panel height inside what fits: at least `min`, at most `maxFraction` of the viewport. */
export const clampPanelHeight = (height, { min = 200, maxFraction = 0.8 } = {}) => {
  const viewport = typeof window !== "undefined" ? window.innerHeight : 1080;
  const max = Math.max(min, Math.round(viewport * maxFraction));
  const wanted = Number.isFinite(height) ? height : min;
  return Math.min(max, Math.max(min, Math.round(wanted)));
};

/** A remembered panel height, or `fallback` when none is stored or storage is unavailable. */
export const readStoredPanelHeight = (key, fallback) => {
  try {
    const stored = Number(window.localStorage.getItem(key));
    return Number.isFinite(stored) && stored > 0 ? stored : fallback;
  } catch {
    return fallback;
  }
};

/** Remember a panel height. Storage failures are ignored — a resize must never throw. */
export const storePanelHeight = (key, height) => {
  try {
    window.localStorage.setItem(key, String(Math.round(height)));
  } catch {
    /* private mode, quota, disabled storage — the height simply is not remembered */
  }
};

export const ResizablePanelHandle = ({
  direction = "horizontal",
  className = "",
  style,
  ...rest
}) => {
  const isHorizontal = direction === "horizontal";
  const gapSize = 4;
  const handleStyle = {
    ...baseHandleStyle,
    ...(isHorizontal
      ? {
          height: "8px",
          minHeight: "8px",
          cursor: "row-resize",
          margin: `${gapSize}px 0`,
        }
      : {
          width: "8px",
          minWidth: "8px",
          cursor: "col-resize",
          margin: `0 ${gapSize}px`,
        }),
    ...style,
  };
  const gripStyle = isHorizontal
    ? { width: "32px", height: "2px" }
    : { width: "2px", height: "32px" };

  return (
    <Separator
      {...rest}
      className={`panel-resize-handle d-flex align-items-center justify-content-center ${className}`}
      style={handleStyle}
    >
      <div
        className="panel-resize-handle__grip"
        style={{ ...baseGripStyle, ...gripStyle }}
      />
    </Separator>
  );
};
